/**
 * Layout generator — three modes:
 *
 * 1. **Q&A conversation** (`layoutQaNext`) — given the conversation so far,
 *    returns the next design question or, when enough answers are collected,
 *    generates a cohesive design role document and writes it to disk.
 *
 * 2. **Refinement** (`generateAndApplyLayout`) — freeform prompt that updates
 *    the existing role document.
 *
 * 3. **Wireframe** (`generateWireframe`) — reads ALL brand files + the role
 *    document and outputs a WireframeData JSON blob for the renderer.
 *
 * Prompt templates live in `layout-prompt.md` and `layout-qa.md` at the repo
 * root (queen-prompt pattern). These are the single source of truth — shared
 * with the user-level scraper role at `~/.appostle/.roles/scraper/`. If the
 * files are missing the loaders throw, by design: a silent fallback masks the
 * spec going AWOL and lets drift accumulate. Future: when the spec stabilizes,
 * the files will move into this package and be served via a daemon endpoint.
 */

import { promises as fs } from "node:fs";
import path from "node:path";
import { fileURLToPath } from "node:url";

import type { Logger } from "pino";
import { z } from "zod";

import type { AgentManager } from "../agent/agent-manager.js";
import {
  DEFAULT_STRUCTURED_GENERATION_PROVIDERS,
  StructuredAgentFallbackError,
  StructuredAgentResponseError,
  generateStructuredAgentResponseWithFallback,
} from "../agent/agent-response-loop.js";
import type { BrandVariable } from "../brands/scanner.js";
import { listBrands } from "../brands/scanner.js";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const QA_FILENAME = "layout-qa.md";
const PROMPT_FILENAME = "layout-prompt.md";
const MAX_LOOKUP_LEVELS = 10;
const ROLE_FILE_RELATIVE = ".appostle/brand/assets/role/brand-layout-role.md";

// ---------------------------------------------------------------------------
// File utilities
// ---------------------------------------------------------------------------

async function findFileUpward(filename: string): Promise<string | null> {
  let dir = path.dirname(fileURLToPath(import.meta.url));
  for (let i = 0; i < MAX_LOOKUP_LEVELS; i++) {
    const candidate = path.join(dir, filename);
    try {
      await fs.access(candidate);
      return candidate;
    } catch {
      // not here
    }
    const parent = path.dirname(dir);
    if (parent === dir) break;
    dir = parent;
  }
  return null;
}

function getRoleFilePath(workspaceRoot: string): string {
  return path.join(workspaceRoot, ROLE_FILE_RELATIVE);
}

async function readRoleFile(workspaceRoot: string): Promise<string | null> {
  try {
    return await fs.readFile(getRoleFilePath(workspaceRoot), "utf8");
  } catch {
    return null;
  }
}

async function writeRoleFile(workspaceRoot: string, content: string): Promise<void> {
  const filePath = getRoleFilePath(workspaceRoot);
  await fs.mkdir(path.dirname(filePath), { recursive: true });
  await fs.writeFile(filePath, content, "utf8");
}

// ---------------------------------------------------------------------------
// Response schemas
// ---------------------------------------------------------------------------

const QaNextResponseSchema = z.object({
  done: z.boolean(),
  question: z.string().optional(),
  options: z.array(z.string()).optional(),
  roleDocument: z.string().optional(),
});

const RefinementResponseSchema = z.object({
  roleDocument: z.string(),
});

const WireframeBlockSchema = z.object({
  type: z.enum(["headline", "subheadline", "text", "image", "cta", "spacer"]),
  widthFraction: z.number(),
  heightVh: z.number(),
});

const WireframeSectionSchema = z.object({
  type: z.enum(["hero", "features", "content", "cta", "footer", "divider"]),
  widthMode: z.enum(["full-bleed", "contained"]),
  heightVh: z.number(),
  arrangement: z.enum(["single-column", "split", "asymmetric-grid", "bento", "stacked"]),
  blocks: z.array(WireframeBlockSchema),
  bg: z.enum(["neutral", "alt", "accent", "image"]),
  gapAfterVh: z.number(),
});

const WireframeDataSchema = z.object({
  viewport: z.object({ width: z.number(), height: z.number() }),
  maxWidth: z.number(),
  pageBg: z.string(),
  sections: z.array(WireframeSectionSchema),
});

// ---------------------------------------------------------------------------
// Brand context builder
// ---------------------------------------------------------------------------

interface BrandEntryLike {
  name: string;
  description: string;
  variables: BrandVariable[];
}

function buildFullBrandContext(allBrands: BrandEntryLike[]): string {
  const lines: string[] = [];
  for (const brand of allBrands) {
    const filled = brand.variables.filter((v) => v.value && v.value.length > 0);
    if (filled.length === 0) continue;
    lines.push(`## ${brand.name}`);
    if (brand.description) lines.push(brand.description);
    for (const v of filled) {
      lines.push(`  ${v.label || v.key}: ${v.value}`);
    }
    lines.push("");
  }
  return lines.length > 0 ? lines.join("\n") : "(No brand context established yet.)";
}

function buildStructuralContext(allBrands: BrandEntryLike[]): string {
  const lines: string[] = [];

  const spacing = allBrands.find((b) => b.name === "spacing");
  if (spacing) {
    const filled = spacing.variables.filter((v) => v.value && v.value.length > 0);
    if (filled.length > 0) {
      lines.push("Spacing (already established):");
      for (const v of filled) lines.push(`  ${v.label || v.key}: ${v.value}px`);
    }
  }

  const typography = allBrands.find((b) => b.name === "typography");
  if (typography) {
    const sizeVars = typography.variables.filter(
      (v) =>
        v.value && v.value.length > 0 && (v.key.includes(".size") || v.key.includes(".fontSize")),
    );
    if (sizeVars.length > 0) {
      lines.push("Typography scale (sizes only):");
      for (const v of sizeVars) lines.push(`  ${v.label || v.key}: ${v.value}`);
    }
  }

  const shapes = allBrands.find((b) => b.name === "shapes");
  if (shapes) {
    const ruleVars = shapes.variables.filter(
      (v) =>
        v.value &&
        v.value.length > 0 &&
        (v.key.includes("rotation") ||
          v.key.includes("utilisation") ||
          v.key.includes("color-rule") ||
          v.key.includes("size-range")),
    );
    if (ruleVars.length > 0) {
      lines.push("Shapes usage rules:");
      for (const v of ruleVars) lines.push(`  ${v.label || v.key}: ${v.value}`);
    }
  }

  return lines.length > 0 ? lines.join("\n") : "(No structural context established yet.)";
}

// ---------------------------------------------------------------------------
// Q&A flow constants
// ---------------------------------------------------------------------------

const TARGET_QUESTIONS = 7;

async function loadSpecFile(filename: string, logger: Logger): Promise<string> {
  const filePath = await findFileUpward(filename);
  if (!filePath) {
    throw new Error(
      `layout-generator: ${filename} not found by walking up from ${fileURLToPath(import.meta.url)}. ` +
        `This file is the canonical spec and must exist at the appostle repo root.`,
    );
  }
  try {
    const content = await fs.readFile(filePath, "utf8");
    logger.debug({ filePath }, `layout-generator: loaded ${filename} from disk`);
    return content;
  } catch (err) {
    throw new Error(
      `layout-generator: failed to read ${filename} at ${filePath}: ${(err as Error).message}`,
    );
  }
}

async function loadQaQuestions(logger: Logger): Promise<string> {
  return loadSpecFile(QA_FILENAME, logger);
}

/**
 * Load the canonical layout prompt template (`layout-prompt.md`) at the repo
 * root. This file is the source of truth for the role document's 17-section
 * output structure and the refinement framing — also mirrored (read-only) by
 * the scraper role. Throws if missing: a silent fallback would let drift hide.
 */
async function loadLayoutPrompt(logger: Logger): Promise<string> {
  return loadSpecFile(PROMPT_FILENAME, logger);
}

/** Substitute `{{key}}` placeholders in a template with the provided values. */
function interpolateTemplate(template: string, vars: Record<string, string>): string {
  let out = template;
  for (const [key, value] of Object.entries(vars)) {
    out = out.split(`{{${key}}}`).join(value);
  }
  return out;
}

function buildQaSystemPrompt(questions: string, layoutPromptSpec: string): string {
  return `You are a chill creative director doing a quick vibe check on someone's layout taste.

CONTEXT:
- You're defining a BRAND — not a product, not a page, not a medium.
- IGNORE the project, repo, product, or workspace. The brand is abstract.
- The layout rules must work across any medium — web, print, PDF, presentation.

Your goal: ask ~${TARGET_QUESTIONS} quick-fire questions to nail down the compositional intent. Then generate the dense role document.

## Question Bank (internal reference — NEVER quote or paraphrase these to the user)
${questions}

## How to ask (THIS IS THE MOST IMPORTANT PART)
The question bank above is technical and heavy. Your job is to translate those topics into SHORT, CASUAL, EFFORTLESS questions. The user should feel like they're picking vibes, not doing homework.

- ONE question at a time. MAX 8 words. Conversational, not formal.
- 4 options, each MAX 6 words. The options carry all the nuance — the user just picks one.
- The options must be concrete and opinionated. Each option should map to a real design decision internally, but READ like a casual preference pick.
- NEVER use technical terms in the question (no "grid", "density", "viewport", "CTA", "breakpoint", "whitespace ratio"). Save that for the role document.
- NEVER use CSS values, property names, or code in questions or options.
- NEVER ask the user to "describe", "name", "list", or "write" anything. They just pick.
- Think Buzzfeed quiz energy, not design school exam.
- Plain language only. No markdown, no parentheticals, no examples.

Good questions: "How packed should things feel?", "What's the vibe up top?", "Cards or open space?"
Bad questions: "What grid-template-columns value reflects your density preference?", "How should the hero section command attention above the fold?"

- After ~${TARGET_QUESTIONS} answers, set done=true and generate the role document.

When done=false, return:
{ "done": false, "question": "Short question?", "options": ["Option A", "Option B", "Option C", "Option D"] }

When done=true, return:
{ "done": true, "roleDocument": "# Brand Layout Role\\n\\n## Enemy\\n..." }

The roleDocument must be a complete markdown document following the structure and rules below. Treat the Q&A conversation as the user's intent; there is no current role document yet.

${layoutPromptSpec}`;
}

// ---------------------------------------------------------------------------
// 1. Q&A next
// ---------------------------------------------------------------------------

export interface LayoutQaNextOptions {
  agentManager: AgentManager;
  workspaceRoot: string;
  brandPath: string;
  conversation: Array<{ role: "assistant" | "user"; content: string }>;
  logger: Logger;
  /** auth-server user-id of the caller — stamped on the internal agent. */
  ownerUserId: string;
}

export interface LayoutQaNextResult {
  done: boolean;
  question?: string;
  options?: string[];
  roleContent?: string;
}

export async function layoutQaNext(options: LayoutQaNextOptions): Promise<LayoutQaNextResult> {
  const { agentManager, workspaceRoot, brandPath, conversation, logger, ownerUserId } = options;

  const brands = await listBrands({ workspaceRoot });
  void brandPath; // API parameter preserved for backward compatibility; context comes from all sections
  if (brands.length === 0) throw new Error("No brand data found — seed a project first");

  const structuralContext = buildStructuralContext(brands);
  const questions = await loadQaQuestions(logger);
  const layoutPromptTemplate = await loadLayoutPrompt(logger);
  // Strip refinement-specific framing — the Q&A completion path generates from
  // scratch, so "current role document" / "user's refinement prompt" sections
  // don't apply. The structural spec (sections, rules, bans) is what we need.
  const layoutPromptSpec = interpolateTemplate(layoutPromptTemplate, {
    structuralContext,
    currentValues:
      "(No existing role document — this is initial generation from a Q&A conversation.)",
    userPrompt: "(See Q&A conversation below for the user's intent.)",
  });
  const qaSystemPrompt = buildQaSystemPrompt(questions, layoutPromptSpec);

  const convLines = conversation
    .map((m) => `${m.role === "assistant" ? "AI" : "User"}: ${m.content}`)
    .join("\n\n");

  const userAnswerCount = conversation.filter((m) => m.role === "user").length;

  const prompt = [
    qaSystemPrompt,
    "",
    "## Structural context",
    structuralContext,
    "",
    userAnswerCount >= TARGET_QUESTIONS
      ? `The user has answered ${userAnswerCount} questions. You MUST now set done=true and generate the complete role document.`
      : `Questions asked so far: ${userAnswerCount}/${TARGET_QUESTIONS}. Ask the next question.`,
    "",
    conversation.length > 0
      ? `## Conversation so far\n\n${convLines}`
      : "## Start\nAsk the first question.",
    "",
    "Return ONLY JSON. No markdown fences. No explanation.",
  ].join("\n");

  const response = await generateStructuredAgentResponseWithFallback({
    manager: agentManager,
    cwd: workspaceRoot,
    prompt,
    schema: QaNextResponseSchema,
    schemaName: "LayoutQaNext",
    maxRetries: 2,
    providers: DEFAULT_STRUCTURED_GENERATION_PROVIDERS,
    ownerUserId,
    agentConfigOverrides: {
      title: "Layout Q&A",
      internal: true,
    },
  });

  if (!response.done) {
    return {
      done: false,
      question: response.question ?? "What compositional direction are you aiming for?",
      options: response.options,
    };
  }

  // Q&A complete — write role document
  if (!response.roleDocument) {
    throw new Error("Q&A marked done but no roleDocument returned");
  }

  await writeRoleFile(workspaceRoot, response.roleDocument);

  logger.info({ brandPath }, "layout Q&A: wrote role document");
  return { done: true, roleContent: response.roleDocument };
}

// ---------------------------------------------------------------------------
// 2. Refinement
// ---------------------------------------------------------------------------

export interface LayoutGenerationOptions {
  agentManager: AgentManager;
  workspaceRoot: string;
  brandPath: string;
  prompt: string;
  logger: Logger;
  /** auth-server user-id of the caller — stamped on the internal agent. */
  ownerUserId: string;
}

export interface LayoutGenerationResult {
  roleContent: string;
}

export async function generateAndApplyLayout(
  options: LayoutGenerationOptions,
): Promise<LayoutGenerationResult> {
  const {
    agentManager,
    workspaceRoot,
    brandPath,
    prompt: userPrompt,
    logger,
    ownerUserId,
  } = options;

  const brands = await listBrands({ workspaceRoot });
  void brandPath; // API parameter preserved for backward compatibility; context comes from all sections
  if (brands.length === 0) throw new Error("No brand data found — seed a project first");

  const structuralContext = buildStructuralContext(brands);
  const existingRole = await readRoleFile(workspaceRoot);
  const layoutPromptTemplate = await loadLayoutPrompt(logger);

  const prompt = interpolateTemplate(layoutPromptTemplate, {
    structuralContext,
    currentValues: existingRole ?? "(No existing role document — generate from scratch.)",
    userPrompt,
  });

  let response: { roleDocument: string };
  try {
    response = await generateStructuredAgentResponseWithFallback({
      manager: agentManager,
      cwd: workspaceRoot,
      prompt,
      schema: RefinementResponseSchema,
      schemaName: "LayoutRefinement",
      maxRetries: 2,
      providers: DEFAULT_STRUCTURED_GENERATION_PROVIDERS,
      ownerUserId,
      agentConfigOverrides: {
        title: "Layout refinement",
        internal: true,
      },
    });
  } catch (error) {
    if (
      error instanceof StructuredAgentResponseError ||
      error instanceof StructuredAgentFallbackError
    ) {
      logger.warn({ err: error, brandPath }, "Structured layout refinement failed");
      throw new Error("Layout refinement failed — the agent did not return valid JSON");
    }
    throw error;
  }

  await writeRoleFile(workspaceRoot, response.roleDocument);

  logger.info({ brandPath }, "layout: wrote refined role document");
  return { roleContent: response.roleDocument };
}

// ---------------------------------------------------------------------------
// 3. Wireframe generation
// ---------------------------------------------------------------------------

export interface WireframeGenerationOptions {
  agentManager: AgentManager;
  workspaceRoot: string;
  brandPath: string;
  logger: Logger;
  /** auth-server user-id of the caller — stamped on the internal agent. */
  ownerUserId: string;
}

const WIREFRAME_SYSTEM_PROMPT = `You are a structural wireframe generator. Given a brand's complete design rules (layout, typography, spacing, colors, shapes, motion) AND a design role document, output a WireframeData JSON object that describes a page skeleton.

The wireframe is a lo-fi structural diagram — it shows ordered sections, their size, arrangement, and content blocks. It must faithfully reflect the layout role document (hero behavior, density, rhythm, grid, bans).

Output ONLY a valid JSON object matching this TypeScript interface:

interface WireframeBlock {
  type: "headline" | "subheadline" | "text" | "image" | "cta" | "spacer";
  widthFraction: number;  // 0-1, fraction of section content area
  heightVh: number;       // fraction of viewport height
}

interface WireframeSection {
  type: "hero" | "features" | "content" | "cta" | "footer" | "divider";
  widthMode: "full-bleed" | "contained";
  heightVh: number;       // fraction of viewport height (1.0 = 100vh)
  arrangement: "single-column" | "split" | "asymmetric-grid" | "bento" | "stacked";
  blocks: WireframeBlock[];
  bg: "neutral" | "alt" | "accent" | "image";
  gapAfterVh: number;    // gap after section in vh fraction
}

interface WireframeData {
  viewport: { width: number; height: number };
  maxWidth: number;       // max content width in px
  pageBg: string;         // CSS color
  sections: WireframeSection[];
}

Rules:
- Include 5-8 sections typical for a landing page
- Respect the role document's hero, density, rhythm, bans
- Section heights should be realistic (hero: 0.8-1.0, features: 0.5-0.8, etc.)
- Block widthFraction values within a section should reflect the arrangement
- Use the brand's spacing/density philosophy to set gapAfterVh values
- Output ONLY the JSON object. No markdown fences.`;

export async function generateWireframe(options: WireframeGenerationOptions): Promise<string> {
  const { agentManager, workspaceRoot, brandPath, ownerUserId } = options;

  const brands = await listBrands({ workspaceRoot });
  void brandPath; // API parameter preserved for backward compatibility; context comes from all sections
  if (brands.length === 0) throw new Error("No brand data found — seed a project first");

  const brandContext = buildFullBrandContext(brands);
  const roleContent = await readRoleFile(workspaceRoot);

  const prompt = [
    WIREFRAME_SYSTEM_PROMPT,
    "",
    "## Complete brand state",
    brandContext,
    "",
    roleContent
      ? `## Design Role Document\n\n${roleContent}`
      : "(No role document found — use brand variables only.)",
  ].join("\n");

  const response = await generateStructuredAgentResponseWithFallback({
    manager: agentManager,
    cwd: workspaceRoot,
    prompt,
    schema: WireframeDataSchema,
    schemaName: "WireframeData",
    maxRetries: 2,
    providers: DEFAULT_STRUCTURED_GENERATION_PROVIDERS,
    ownerUserId,
    agentConfigOverrides: {
      title: "Wireframe generator",
      internal: true,
    },
  });

  return JSON.stringify(response);
}
